/**
 * Profile Photo Field
 *
 * Admin control for uploading a user's profile photo with a LinkedIn-style
 * circular crop. The admin picks an image (which may be wide), then zooms/pans
 * it inside a circular mask that previews exactly how it will be stored and
 * shown across the platform.
 *
 * Two modes:
 *  - Immediate (edit): `userId` is provided → upload/remove hit the backend
 *    right away and report the updated user via `onChange`.
 *  - Deferred (create): no `userId` yet → the cropped Blob is reported via
 *    `onPendingChange` so the parent can upload it after creating the user.
 */

import { useCallback, useRef, useState } from 'react';
import { Button } from '../../components/button';
import type { ButtonVariant } from '../../components/button';
import { Modal } from '../../components/modal';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { useUploadUserPhoto, useRemoveUserPhoto } from '../../api/admin/users/hooks';
import type { UserListItem } from '../../api/admin/users/types';
import defaultAvatar from '../../assets/icons/avatars/default-avatar.svg';
import styles from './profile-photo-field.module.css';

interface ProfilePhotoFieldProps {
  /** Existing photo URL (edit mode). */
  currentUrl?: string | null;
  /** When set, upload/remove happen immediately against this user. */
  userId?: string;
  /** Immediate mode: called with the updated user after upload/remove. */
  onChange?: (user: UserListItem) => void;
  /** Deferred mode (create): reports the cropped blob (or null when cleared). */
  onPendingChange?: (blob: Blob | null) => void;
  /**
   * Emphasis for the pick/change button. Admin uses the quiet default; the
   * guardian area passes `gold` so it matches that area's prestige CTA.
   *
   * This is a prop rather than a CSS override because the button is a CSS
   * Module — its class name is build-scoped, so a stylesheet elsewhere can no
   * longer reach in and re-skin it. Context-dependent styling has to be asked
   * for explicitly, which is the point.
   */
  buttonVariant?: ButtonVariant;
}

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export function ProfilePhotoField({ currentUrl, userId, onChange, onPendingChange, buttonVariant = 'ghost' }: ProfilePhotoFieldProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadPhoto = useUploadUserPhoto();
  const removePhoto = useRemoveUserPhoto();

  // Source image being cropped (data URL); null when the cropper is closed.
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  // Local preview of a freshly cropped image (deferred/create mode).
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewSrc = localPreview || currentUrl || defaultAvatar;
  const hasPhoto = Boolean(localPreview || currentUrl);

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    // Allow re-selecting the same file later.
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(t('admin.users.form.photo.invalidType'));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError(t('admin.users.form.photo.tooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const closeCropper = () => {
    setImageSrc(null);
    setCroppedAreaPixels(null);
  };

  const handleSaveCrop = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
      if (userId) {
        // Immediate mode — upload now.
        const updated = await uploadPhoto.mutateAsync({ id: userId, blob });
        onChange?.(updated);
        setLocalPreview(null); // the updated user's URL drives the preview
      } else {
        // Deferred mode — keep the blob for the parent to upload after create.
        if (localPreview) URL.revokeObjectURL(localPreview);
        setLocalPreview(URL.createObjectURL(blob));
        onPendingChange?.(blob);
      }
      closeCropper();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.users.form.photo.uploadError'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    setError(null);
    try {
      if (userId && currentUrl) {
        const updated = await removePhoto.mutateAsync(userId);
        onChange?.(updated);
      }
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
        setLocalPreview(null);
      }
      onPendingChange?.(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.users.form.photo.uploadError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.profilePhotoField}>
      <img src={previewSrc} alt="" className={`${styles.profilePhotoField__preview}`} />
      <div className={`${styles.profilePhotoField__controls}`}>
        <Button type="button" variant={buttonVariant} size="sm" onClick={() => fileInputRef.current?.click()}
          disabled={busy}
        >
          {hasPhoto ? t('admin.users.form.photo.change') : t('admin.users.form.photo.choose')}
        </Button>
        {hasPhoto && (
          <Button type="button" variant="dangerQuiet" size="sm" onClick={() => void handleRemove()}
            disabled={busy}
          >
            {t('admin.users.form.photo.remove')}
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFilePicked}
          style={{ display: 'none' }}
        />
      </div>
      {error && <span className={`${styles.profilePhotoField__error}`}>{error}</span>}

      {/* `layer="confirm"` because the cropper opens from inside the user form
          dialog and has to sit above it. */}
      <Modal
        isOpen={Boolean(imageSrc)}
        onClose={closeCropper}
        size="sm"
        layer="confirm"
        title={t('admin.users.form.photo.cropTitle')}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={closeCropper} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={() => void handleSaveCrop()} disabled={busy}>
              {busy ? t('common.loading') : t('admin.users.form.photo.apply')}
            </Button>
          </>
        }
      >
        <p className={`${styles.profilePhotoCropper__hint}`}>{t('admin.users.form.photo.cropHint')}</p>
        <div className={`${styles.profilePhotoCropper__area}`}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <label className={`${styles.profilePhotoCropper__zoom}`}>
          {t('admin.users.form.photo.zoom')}
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </label>
      </Modal>
    </div>
  );
}

/**
 * Crops the given image (data URL) to the selected pixel area and returns a PNG
 * Blob. The backend re-encodes to a 512x512 WebP, so we only need a faithful
 * square crop here.
 */
async function getCroppedBlob(imageSrc: string, area: Area): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = area.width;
  canvas.height = area.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    area.width,
    area.height,
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to crop image'))),
      'image/png',
    );
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
